import { Session } from '@aspendental/shared-components-web';
import Bugsnag from '@bugsnag/js';
import { BugsnagPluginReactResult } from '@bugsnag/plugin-react';
import { Faro, FaroErrorBoundary } from '@grafana/faro-react';
import { NextPage } from 'next';
import { appWithTranslation } from 'next-i18next';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { useRouter } from 'next/router';
import React, { ReactElement, ReactNode, useEffect, useRef } from 'react';
import Cookies from 'universal-cookie';

import { ContextProviders } from '@src/components/shared/providers/ContextProviders';
import { Layout } from '@src/components/templates/layout';
import { getLogger } from '@src/lib/logging/logger';
import { initGrafanaFaro } from '@src/services/faro';
import { checkQueryParam } from '@src/utils/check-query-param';

import '@aspendental/shared-components-web/lib/fonts.css';
import '@contentful/live-preview/style.css';
import { initBugsnag } from '@src/services/bugsnag';

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
	getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
	Component: NextPageWithLayout;
};

export function reportWebVitals({ id, name, label, value }: NextWebVitalsMetric) {
	const logger = getLogger('client');
	logger.info({
		name,
		category: label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
		value: Math.round(name === 'CLS' ? value * 1000 : value), // values must be integers
		id, // id unique to current page load
	});
}

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
	const config = pageProps?.config as BrandThemeConfig;
	const { features } = config as BrandThemeConfig;

	const router = useRouter();
	initBugsnag(pageProps.env);

	const BugsnagReactPlugin = Bugsnag.getPlugin('react') as BugsnagPluginReactResult;
	const BugsnagErrorBoundary = BugsnagReactPlugin.createErrorBoundary(React);

	const faroRef = useRef<null | Faro>(null);

	useEffect(() => {
		if (!faroRef.current && features?.realUserMonitoring?.enabled) {
			faroRef.current = initGrafanaFaro(features.realUserMonitoring.faroApiKey);
		}
	}, [features?.realUserMonitoring]);

	useEffect(() => {
		// when component is mounting we remove server side rendered css:
		const jssStyles = document.querySelector('#jss-server-side');

		// Store the rwg_token in a cookie
		const cookies = new Cookies();

		//google reserve cookie
		const { rwg_token } = router.query;
		//get original referrer facility code
		const { fc } = router.query;

		let cookieString = '';

		if (rwg_token) {
			if (checkQueryParam('rwg_token')) {
				cookieString += rwg_token;
				cookies.set('_rwg_token', rwg_token);
			}
		}
		if (fc) {
			if (checkQueryParam('fc')) {
				cookieString += ';_merchant_id=' + fc;
				cookies.set('facility-code', fc);
			}
		}

		if (cookieString.length > 0) {
			cookieString += ';max-age=2592000;domain=aspendental.com;path=/schedule-an-appointment';
			// cookies.set('_rwg_token', cookieString);
		}

		if (jssStyles?.parentNode) {
			jssStyles.parentNode.removeChild(jssStyles);
		}

		router.events.on('routeChangeComplete', () => null);

		return () => {
			router.events.off('routeChangeComplete', () => null);
		};
	}, [router.events]);

	if (router.pathname === '_error') return <Component {...pageProps} />;

	const getLayout =
		Component.getLayout ??
		((page) => {
			return (
				<Layout
					locale={router.locale ?? 'en-US'}
					preview={pageProps.preview}
					appData={pageProps.appData}
					isFooterLite={features.footer.lite}
					withSimpleLayout={features.footer.withSimpleLayout}
					footerData={pageProps.footerData}
					logoHref={
						pageProps.env?.ENVIRONMENT_NAME === 'prod'
							? pageProps.config.features.livePreview.prod
							: pageProps.config.features.livePreview.stage
					}
				>
					{page}
				</Layout>
			);
		});

	return (
		<FaroErrorBoundary>
			<BugsnagErrorBoundary>
				<Session>
					<ContextProviders
						config={pageProps.config}
						preview={pageProps.preview}
						environment={pageProps.env?.ENVIRONMENT_NAME}
						graphURL={pageProps.graphURL}
						user={pageProps.user}
						googleURL={pageProps.googleURL}
						resultURL={pageProps.resultURL}
						googleMapApiKey={pageProps.googleMapApiKey}
						eapiBaseUrl={pageProps.eapiBaseUrl}
						userEventsURL={pageProps.userEventsURL}
						appData={pageProps.appData}
					>
						{getLayout(<Component {...pageProps} />)}
					</ContextProviders>
				</Session>
			</BugsnagErrorBoundary>
		</FaroErrorBoundary>
	);
};

export default appWithTranslation(App);
